import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, Alert, Divider, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthProvider';
import { useSelectedLadderOrdersStore } from '../Store/selectedLadderOrdersStore';
import { useBondCalculationStore } from '../Store/bondCalculationStore'; 
import SelectedOrdersPreviewTable from '../Component/BondLadderComponents/SelectedOrdersPreviewTable';
import BondLadderHeader from '../Component/BondLadderComponents/BondLadderHeader';
import PriceCommentModal from '../Component/BondLadderComponents/PriceCommentModal';
import BondOrderProcessor from '../Component/BondLadderComponents/BondOrderProcessor';
import { placeBulkOrders } from '../http/api';
import dayjs from 'dayjs';
import useSnackbar from '../Utils/useSnackbar';
import { calculateOrderFinancials } from '../Utils/ladderCaclulations';
import { tokens, withAlpha } from '../Common/designTokens'; // added

const BondLadderPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, userId } = useAuth();
  const { selectedOrders, clearAllOrders, selectOrder, originalSelectedOrders } = useSelectedLadderOrdersStore();
  const { 
    calculateBondValues, 
    calculateBondValuesInBatch,
    getPendingRequestsByRungIndex, 
    getPendingUUIDs, 
    timeoutMessages,
    clearAllTimeoutMessages
  } = useBondCalculationStore();
  
  const { bondLadderId, ladderData } = location.state || {};
  const [faceValue, setFaceValue] = useState(1);
  const [orderError, setOrderError] = useState(''); // Add order error state
  
  const { SnackbarComponent, showError } = useSnackbar();

  const [selectedRowForModal, setSelectedRowForModal] = useState(null);
  const [selectedRungIndex, setSelectedRungIndex] = useState(null);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalQtys, setOriginalQtys] = useState({}); // <-- new state
  const initialCalcComplete = useRef(false);
  const TIMEOUT_DURATION = 10000; // 10 seconds in milliseconds

  // Wrapper around shared utility function to maintain component-specific context
  const calculateBondFinancials = useCallback((order, logPrefix = '') => {
    return calculateOrderFinancials(
      order,
      ladderData.amountPerRung,
      faceValue,
      logPrefix
    );
  }, [ladderData, faceValue]);

  const selectedOrdersArray = Object.entries(selectedOrders).map(([rungIndex, order]) => ({
    ...order,
    rungIndex,
  }));

  // Build processed orders but respect manualQty (if user manually set qty)
  const processedOrdersArray = selectedOrdersArray.map(order => {
    const logPrefix = `[ProcessOrder:${order.rungIndex}] `;
    // Use shared util to get costPerUnit (and default qty if needed)
    const calc = calculateBondFinancials(order, logPrefix);
    const costPerUnit = calc.costPerUnit ?? 0;

    // If user manually set qty, use that value; otherwise use calculated qty
    if (order.manualQty && typeof order.qty !== 'undefined' && order.qty !== null) {
      const manualQty = Number(order.qty) || 0;
      const totalCost = costPerUnit * manualQty;
      return { ...order, qty: manualQty, totalCost };
    }

    // default calculated path
    return { ...order, qty: calc.qty, totalCost: calc.totalCost };
  });

  const defaultColumns = [
    { key: "cusip", name: "CUSIP" },
    { key: "description", name: "Description" },
    { key: "comments", name: "Comments" },
    { key: "ytm", name: "Yield" },
    { key: "accruedInterest", name: "Accrued Interest" },
    { key: "price", name: "Price" },
    { key: "qty", name: "Quantity" },
    { key: "totalCost", name: "Total Cost" },
  ];

  // Compute total portfolio cost from processedOrdersArray so manual qty is honored
  const totalPortfolioCost = processedOrdersArray.reduce((sum, o) => sum + (Number(o.totalCost) || 0), 0);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleNavigateAfterSuccess = () => {
    navigate('/order?view=0&tab=0');
  };
  useEffect(() => {
    //if dselected orders data is not available, redirect to /order?view=0&tab=2
    if (!selectedOrders || Object.keys(selectedOrders).length === 0) {
      console.warn('No selected orders found, redirecting to order page');
      navigate('/order?view=0&tab=2');
      return;
    }
  }, []);

  useEffect(() => {
    const prices = {};
    const qtys = {}; // collect original (rounded) qtys

    Object.entries(selectedOrders).forEach(([rungIndex, order]) => {
      // Use existence check so 0 is respected
      if (!Object.prototype.hasOwnProperty.call(originalPrices, rungIndex)) {
        prices[rungIndex] = order.price;
      }

      if (!Object.prototype.hasOwnProperty.call(originalQtys, rungIndex)) {
        try {
          // Compute with the shared util; avoid depending on the callback identity
          const calc = calculateOrderFinancials(
            order,
            ladderData?.amountPerRung,
            faceValue,
            `[OriginalQty:${rungIndex}] `
          );
          qtys[rungIndex] = (calc && calc.qty != null) ? calc.qty : (order.qty ?? 0);
        } catch {
          // Fallback to whatever was present on the order
          qtys[rungIndex] = order.qty ?? 0;
        }
      }
    });

    if (Object.keys(prices).length > 0) {
      setOriginalPrices(prev => ({ ...prev, ...prices }));
    }
    if (Object.keys(qtys).length > 0) {
      setOriginalQtys(prev => ({ ...prev, ...qtys }));
    }
  }, [
    selectedOrders,
    originalPrices,
    originalQtys,
    ladderData?.amountPerRung, // primitive dependency
    faceValue                     // primitive dependency
  ]);

  const handleSelectOrder = useCallback((rungIndex, orderData) => {
    selectOrder(rungIndex, orderData);
  }, [selectOrder]);

  useEffect(() => {
    const timeoutChecker = setInterval(() => {
      const pendingByRung = getPendingRequestsByRungIndex();
      const now = Date.now();
      
      Object.entries(pendingByRung).forEach(([rungIndex, requestData]) => {
        const { timestamp, uuid, originalValues } = requestData;
        
        if (now - timestamp > TIMEOUT_DURATION) {
          console.log(`Request ${uuid} for rung ${rungIndex} timed out after ${TIMEOUT_DURATION/1000} seconds`);
          
          useBondCalculationStore.getState().markRequestTimedOut(uuid);
          
          if (originalValues && selectedOrders[rungIndex]) {
            handleSelectOrder(rungIndex, {
              ...selectedOrders[rungIndex],
              ytm: originalValues.ytm,
              accruedInterest: originalValues.accruedInterest
            });
            console.log(`Reverted yield and accrued interest for rung ${rungIndex}:`, {
              ytm: originalValues.ytm,
              accruedInterest: originalValues.accruedInterest
            });
          }
        }
      });
    }, 1000);
    
    return () => clearInterval(timeoutChecker);
  }, [getPendingRequestsByRungIndex, selectedOrders, handleSelectOrder]);

  useEffect(() => {
    const handleBondCalcUpdate = (event) => {
      const calcData = event.detail;
      console.log(`Response received for UUID ${calcData.UUID}`);
      
      const bondCalcStore = useBondCalculationStore.getState();
      
      const pendingByRung = bondCalcStore.getPendingRequestsByRungIndex();
      const match = Object.entries(pendingByRung).find(
        ([_, data]) => data.uuid === calcData.UUID
      );
      
      const timeouts = bondCalcStore.timeoutMessages;
      
      if (match) {
        const [rungIndex] = match;
        const rungIndexNum = parseInt(rungIndex);
        
        if (timeouts[rungIndexNum]) {
          console.log(`Ignoring late response for UUID ${calcData.UUID} (rung ${rungIndexNum + 1}) - request already timed out`);
          
          bondCalcStore.removePendingRequest(calcData.UUID);
          return;
        }
        
        bondCalcStore.storeCalculationResult(calcData.UUID, calcData);
        
        const currentOrder = selectedOrders[rungIndexNum];
        if (!currentOrder) return;
        
        const newAccruedInterest = parseFloat(calcData.Accr_Interest) || 0;
        
        const updatedOrder = {
          ...currentOrder,
          accruedInterest: newAccruedInterest,
          ytm: calcData.Yield,
          rungIndex: rungIndexNum
        };
        
        const logPrefix = `[UpdateCalc:${rungIndexNum}] `;
        console.log(`${logPrefix}Bond calculation result:`, {
          uuid: calcData.UUID,
          yield: calcData.Yield,
          accruedInterest: newAccruedInterest,
          previousAccruedInterest: currentOrder.accruedInterest,
          previousYield: currentOrder.ytm
        });
        
        const { qty } = calculateBondFinancials(updatedOrder, logPrefix);
        updatedOrder.qty = qty;
        
        handleSelectOrder(rungIndexNum, updatedOrder);
        
        bondCalcStore.removePendingRequest(calcData.UUID);
        
        console.log(`${logPrefix}Order updated with new values`);
      }
    };

    const handleBondCalcError = (event) => {
      const errorData = event.detail;
      console.error(`Calculation error for UUID ${errorData.UUID}:`, errorData.message);
      
      useBondCalculationStore.getState().removePendingRequest(errorData.UUID);
    };

    window.addEventListener("bondCalcUpdate", handleBondCalcUpdate);
    window.addEventListener("bondCalcError", handleBondCalcError);

    return () => {
      window.removeEventListener("bondCalcUpdate", handleBondCalcUpdate);
      window.removeEventListener("bondCalcError", handleBondCalcError);
    };
  }, [selectedOrders, handleSelectOrder, calculateBondFinancials]);

  useEffect(() => {
    const runInitialCalculations = async () => {
      // If coming from edit (originalSelectedOrders is not empty), skip calculation
      // if (originalSelectedOrders && Object.keys(originalSelectedOrders).length > 0) {
      //   initialCalcComplete.current = true;
      //   return;
      // }
      if (initialCalcComplete.current || Object.keys(selectedOrders).length === 0) return;
      
      console.log('Starting initial bond calculations for orders in batch');
      
      const batchRequests = Object.entries(selectedOrders)
        .filter(([_, order]) => order.cusip && order.price)
        .map(([rungIndex, order]) => ({
          rungIndex,
          cusip: order.cusip,
          price: order.price,
          originalValues: {
            accruedInterest: order.accruedInterest,
            ytm: order.ytm
          }
        }));
      
      if (batchRequests.length > 0) {
        await calculateBondValuesInBatch(token, userId, batchRequests);
      }
      
      initialCalcComplete.current = true;
    };

    runInitialCalculations();
    return () => {
      if (initialCalcComplete.current) {
        useBondCalculationStore.getState().clearAll();
      }
    };
  }, []);

  const handleRowClick = (row, rungIndex) => {
    // Check if the order is editable
    if (row.isEditable === false) {
      return; // Prevent opening modal for non-editable orders
    }
    
    setSelectedRowForModal({
      ...row,
      minQty: row.minQty, // <-- ensure minQty is present for the modal
      originalPrice: originalPrices[rungIndex] || row.price,
      originalQty: originalQtys[rungIndex] ?? row.qty // <-- include original qty
    });
    setSelectedRungIndex(rungIndex);
    setIsPriceModalOpen(true);
  };

  const handlePriceCommentSubmit = async (updatedRow) => {
    console.log('Price/Comment updated:', updatedRow);

    const currentOrder = selectedOrders[selectedRungIndex];
    const priceChanged = currentOrder && Number(updatedRow.price) !== Number(currentOrder.price);
    const qtyChanged = currentOrder && (Number(updatedRow.qty || 0) !== Number(currentOrder.qty || 0));

    // If qty changed, ensure manual flag is set
    if (qtyChanged) {
      updatedRow.manualQty = true;
    }

    // Preserve existing fields like minQty when updating
    handleSelectOrder(selectedRungIndex, {
      ...currentOrder,
      ...updatedRow
    });

    // Only trigger remote calculation if the price changed (qty-only changes do not need recalculation)
    if (priceChanged) {
      const originalValues = selectedOrders[selectedRungIndex] ? {
        accruedInterest: selectedOrders[selectedRungIndex].accruedInterest,
        ytm: selectedOrders[selectedRungIndex].ytm
      } : null;

      await calculateBondValues(
        token,
        userId,
        updatedRow.cusip,
        updatedRow.price,
        selectedRungIndex,
        originalValues
      );

      useBondCalculationStore.getState().clearTimeoutMessage(selectedRungIndex);
    }

    setIsPriceModalOpen(false);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === 'N/A') return 'N/A';
    return Number(value).toLocaleString('en-US');
  };

  const calculateAveragePrice = () => {
    const orders = Object.values(selectedOrders);
    if (orders.length === 0) return 0;
    
    const totalPrice = orders.reduce((sum, order) => {
      return sum + (parseFloat(order.price) || 0);
    }, 0);
    
    return totalPrice / orders.length;
  };

  const pendingRequests = getPendingRequestsByRungIndex();
  const pendingCalcUUIDs = getPendingUUIDs();
  const averagePrice = calculateAveragePrice();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 84px)',
      width: '100%',
      minWidth: '600px',
      backgroundColor: tokens.colors.bodyBackgroundColor,
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '16px',
        borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
        backgroundColor: tokens.colors.defaultGridRowColor,
        gap: "10px"
      }}>
        <Typography
          variant="h5"
          sx={{
            color: tokens.colors.fontColour,
            fontWeight: 'bold',
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
          }}
        >
          {ladderData?.fromStagingEdit ? "Restaged Order Preview" : "Order Preview"}
        </Typography>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          minHeight: '24px',
        }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              color: tokens.palette.primary
            }}
          >
            {`Name: ${ladderData?.userName || 'N/A'}`}
          </Typography>

          <Box component="span" sx={{ height: '18px', width: '1px', backgroundColor: tokens.colors.bodyBorderColor50, mx: 0.2 }} />

          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              color: tokens.palette.primary
            }}
          >
            {`Account: ${ladderData?.account || 'N/A'}`}
          </Typography>

          <Box component="span" sx={{ height: '18px', width: '1px', backgroundColor: tokens.colors.bodyBorderColor50, mx: 0.2 }} />

          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              color: tokens.palette.primary
            }}
          >
            {`Side: ${ladderData?.side}`}
          </Typography>

          <Box component="span" sx={{ height: '18px', width: '1px', backgroundColor: tokens.colors.bodyBorderColor50, mx: 0.2 }} />

          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              color: tokens.palette.primary
            }}
          >
          Total Amt: {
            ladderData.qty
              ? Number(ladderData.qty).toLocaleString('en-US')
              : (ladderData.totalAmount
                  ? Number(ladderData.totalAmount).toLocaleString('en-US')
                  : ladderData.qty)
          }</Typography>

           {ladderData?.totalMaturity && (
             <>
              <Box component="span" sx={{ height: '18px', width: '1px', backgroundColor: tokens.colors.bodyBorderColor50, mx: 0.2 }} />

              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                  color: tokens.palette.primary
                }}
              >
                {`Total Maturity: ${formatNumber(ladderData.totalMaturity)} Month${ ladderData.totalMaturity > 1 ? 's' : ''}`}
              </Typography>
             </>
           )}
         </Box>
       </Box>

       <Box sx={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        backgroundColor: tokens.colors.bodyBackgroundColor,
        width: '100%',
       }}>
         <SelectedOrdersPreviewTable
           selectedColumns={defaultColumns}
           bondLadderId={bondLadderId}
           ladderData={ladderData}
           showPreviewOnly={true}
           maxTableHeight="calc(100vh - 296px)"
           calculateTotalCost={true}
           onRowClick={handleRowClick}
           faceValue={faceValue}
           pendingRequests={pendingRequests}
           pendingCalcUUIDs={pendingCalcUUIDs}
           timeoutMessages={timeoutMessages}
         />
         <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          p: 1,
          backgroundColor: 'transparent',
          borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
          gap: 3,
         }}>
           <Box sx={{ display: 'flex', alignItems: 'center' }}>
             <Typography sx={{ color: tokens.colors.fontColour, fontWeight: 700, fontSize: '1.1rem', marginRight: 2 }}>
               Average Price:
             </Typography>
             <Typography sx={{
              color: tokens.palette.primary,
              fontWeight: 700,
              fontSize: '1.1rem',
              backgroundColor: withAlpha(tokens.palette.primary, 0.16),
              padding: '6px 12px',
              borderRadius: '8px',
             }}>
               ${averagePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
             </Typography>
           </Box>
           
           <Box sx={{ display: 'flex', alignItems: 'center' }}>
             <Typography sx={{ color: tokens.colors.fontColour, fontWeight: 700, fontSize: '1.1rem', marginRight: 2 }}>
               Ladder Cost:
             </Typography>
             <Typography sx={{
              color: tokens.palette.primary,
              fontWeight: 700,
              fontSize: '1.1rem',
              backgroundColor: withAlpha(tokens.palette.primary, 0.16),
              padding: '6px 12px',
              borderRadius: '8px',
             }}>
               ${totalPortfolioCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
             </Typography>
           </Box>
         </Box>
       </Box>

       <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        p: 2,
        backgroundColor: tokens.colors.defaultGridRowColor,
        borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
       }}>
         <Button
           variant="outlined"
           onClick={handleGoBack}
           sx={{
             textTransform: "none",
             fontWeight: "700",
             px: 2,
             py: 1,
             width: "180px",
             height: "40px",
             borderRadius: "8px",
             color: tokens.colors.fontColour,
             borderColor: tokens.colors.bodyBorderColor50,
             '&:hover': {
               borderColor: tokens.colors.bodyBorderColor50,
               backgroundColor: tokens.colors.defaultGridHoverColor,
             }
           }}
         >
           Edit Ladder Orders
         </Button>
         
         <BondOrderProcessor 
           token={token}
           selectedOrders={selectedOrders}
           bondLadderId={bondLadderId}
           ladderData={ladderData}
           onOrdersCleared={clearAllOrders}
           onNavigateAfterSuccess={handleNavigateAfterSuccess}
           selectedOrdersArray={processedOrdersArray}
           buttonDisabled={processedOrdersArray.length === 0}
           onError={(error) => {
             setOrderError(error);
             showError(error);
           }}
         />
       </Box>

       <PriceCommentModal
         open={isPriceModalOpen}
         onClose={() => setIsPriceModalOpen(false)}
         selectedRow={selectedRowForModal}
         rungIndex={selectedRungIndex}
         onSubmit={handlePriceCommentSubmit}
       />

       <SnackbarComponent />
     </Box>
   );
 };

 export default BondLadderPreview;